import {IPluginsQuery, pluginsQueryToAPIRequestParams} from "@hooks/plugins/PluginsDataHooks";
import {useEffect, useState} from "react";
import {LanguageOption, TagOption} from "@components/elements/FiltersToolbar";
import {IProcessedPluginData} from "@hooks/plugins/IProcessedPluginsData";
import {RequestsControllerService} from "@utils/requestsControllerService";
import {Plugins} from "@hooks/plugins/plugins";
import {IPluginsSearchData} from "@components/widgets/PluginsList";
import isEqual from "lodash.isequal";
import {buildApiUrl} from "@utils/api";

const buildFacetQuery = (pluginsQuery?: IPluginsQuery): IPluginsQuery | undefined => {
    if (!pluginsQuery) {
        return undefined;
    }
    const {page, sortColumn, sortDescending, ...facetQuery} = pluginsQuery;
    return facetQuery;
};

export const usePluginsSearch = (pluginsQuery: IPluginsQuery | undefined, skipInitialSearch: boolean = true): IPluginsSearchData => {

    const [lastPluginsQuery, setLastPluginsQuery] = useState<IPluginsQuery | undefined>(skipInitialSearch ? pluginsQuery : undefined);
    const [lastFacetQuery, setLastFacetQuery] = useState<IPluginsQuery | undefined>(skipInitialSearch ? buildFacetQuery(pluginsQuery) : undefined);
    const [tags, setTags] = useState<TagOption[]>();
    const [languages, setLanguages] = useState<LanguageOption[]>();
    const [plugins, setPlugins] = useState<IProcessedPluginData[]>();
    const [loadingTags, setLoadingTags] = useState<boolean>(false);
    const [loadingLanguages, setLoadingLanguages] = useState<boolean>(false);
    const [loadingPlugins, setLoadingPlugins] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPlugins, setTotalPlugins] = useState<number>(0);
    const [pluginsPerPage, setPluginsPerPage] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [gettersThrottlingTimeout, setGettersThrottlingTimeout] = useState<any>();


    const performRequest = async (url: string, requestType: string, passedOptions?: any) => {

        RequestsControllerService.getInstance().clearControllers(requestType);
        const controller = new AbortController();
        RequestsControllerService.getInstance().addController(requestType, controller);

        const options = {
            ...passedOptions,
            signal: controller.signal
        }

        return fetch(url, (options) ? options : {}).then(response => response.json()).then(data => {
                return data;
            }
        )
    }

    const getPlugins = async () => {
        if (!pluginsQuery) {
            return;
        }
        setLoadingPlugins(true);
        let url = buildApiUrl('/plugins');
        const params = pluginsQueryToAPIRequestParams(pluginsQuery, new URLSearchParams());
        url += '?' + params.toString();
        performRequest(url, 'plugins').then(pluginsDataResponse => {
            const pluginsData = Plugins.processApiResponse(pluginsDataResponse);
            if (!pluginsData) {
                console.warn('pluginsData no data')
                console.debug(pluginsDataResponse)
                return;
            }
            setPlugins(pluginsData.plugins);
            setTotalPages(pluginsData.total_pages);
            setTotalPlugins(pluginsData.total_plugins);
            setCurrentPage(pluginsData.current_page);
            setPluginsPerPage(pluginsData.plugins_per_page);
            setLoadingPlugins(false);
        }).catch(error => {
            if (error.name === 'AbortError') {
                return;
            }
            console.warn('pluginsData error')
            console.debug(error)
        });
    }

    const getTags = async () => {
        const facetQuery = buildFacetQuery(pluginsQuery);
        if (!facetQuery) {
            return;
        }
        setLoadingTags(true);
        let url = buildApiUrl('/plugins/tags');
        const params = pluginsQueryToAPIRequestParams(facetQuery, new URLSearchParams());
        url += '?' + params.toString();
        performRequest(url, 'tags').then(tagsResponse => {
            const tags = tagsResponse.data.map((tagAPIData: any): TagOption => {
                return new TagOption(tagAPIData.slug, tagAPIData.label + ' (' + tagAPIData.plugins_count + ')');
            });
            setTags(tags);
            setLoadingTags(false);
        }).catch(error => {
            if (error.name === 'AbortError') {
                return;
            }
            console.warn('tags error')
            console.log(error)
        });
    }

    const getLanguages = async () => {
        const facetQuery = buildFacetQuery(pluginsQuery);
        if (!facetQuery) {
            return;
        }
        setLoadingLanguages(true);
        let url = buildApiUrl('/plugins/languages');
        const params = pluginsQueryToAPIRequestParams(facetQuery, new URLSearchParams());
        url += '?' + params.toString();
        performRequest(url, 'languages').then(languagesResponse => {
            const languages = languagesResponse.data.map((languageAPIData: any): LanguageOption => {
                return new LanguageOption(languageAPIData.slug, languageAPIData.english_name + ' (' + languageAPIData.plugins_count + ')');
            });
            setLanguages(languages);
            setLoadingLanguages(false);
        }).catch(error => {
            if (error.name === 'AbortError') {
                return;
            }
            console.warn('languages error')
            console.debug(error)
        });
    }

    useEffect(() => {
        const facetQuery = buildFacetQuery(pluginsQuery);
        const pluginsChanged = !isEqual(lastPluginsQuery, pluginsQuery);
        const facetsChanged = !isEqual(lastFacetQuery, facetQuery);

        if (!pluginsChanged && !facetsChanged) {
            return;
        }

        const runSearches = () => {
            if (pluginsChanged) {
                getPlugins();
            }
            if (facetsChanged) {
                getTags();
                getLanguages();
            }
        };

        // if it's the search term that is changing, wait 1.5s before performing the search
        if (lastPluginsQuery && lastPluginsQuery.search !== pluginsQuery?.search) {
            if (gettersThrottlingTimeout) {
                clearTimeout(gettersThrottlingTimeout);
            }
            setGettersThrottlingTimeout(setTimeout(() => {
                runSearches();
            }, 1500));
        } else {
            runSearches();
        }

        setLastPluginsQuery(pluginsQuery);
        setLastFacetQuery(facetQuery);
    }, [pluginsQuery]);

    return {
        tags,
        languages,
        plugins,
        currentPage,
        totalPlugins,
        pluginsPerPage,
        totalPages,
        loadingTags,
        loadingLanguages,
        loadingPlugins
    };
}


export const ssrUsePluginsSearch = async (pluginsQuery: IPluginsQuery | undefined, origin?: string, nowMs?: number): Promise<IPluginsSearchData> => {
    const emptyResponse = {
        tags: [],
        languages: [],
        plugins: [],
        currentPage: 1,
        totalPlugins: 0,
        pluginsPerPage: 0,
        totalPages: 0,
        loadingTags: false,
        loadingLanguages: false,
        loadingPlugins: false
    };

    if (!pluginsQuery) {
        return emptyResponse;
    }

    try {
        let url = buildApiUrl('/plugins', origin);
        const params = pluginsQueryToAPIRequestParams(pluginsQuery, new URLSearchParams());
        url += '?' + params.toString();
        const pluginsDataResponse = await fetch(url).then(response => response.json());
        const pluginsData = Plugins.processApiResponse(pluginsDataResponse, nowMs);
        if (!pluginsData) {
            console.warn('pluginsData no data');
            console.debug(pluginsDataResponse);
            return emptyResponse;
        }

        let tags: TagOption[] = [];
        try {
            url = buildApiUrl('/plugins/tags', origin);
            const tagsResponse = await fetch(url).then(response => response.json());
            tags = tagsResponse.data.map((tagAPIData: any): TagOption => {
                return new TagOption(tagAPIData.slug, tagAPIData.label + ' (' + tagAPIData.plugins_count + ')');
            });
        } catch (error) {
            console.warn('Error fetching tags:', error);
        }

        let languages: LanguageOption[] = [];
        try {
            url = buildApiUrl('/plugins/languages', origin);
            const languagesResponse = await fetch(url).then(response => response.json());
            languages = languagesResponse.data.map((languageAPIData: any): LanguageOption => {
                return new LanguageOption(languageAPIData.slug, languageAPIData.english_name + ' (' + languageAPIData.plugins_count + ')');
            });
        } catch (error) {
            console.warn('Error fetching languages:', error);
        }

        return {
            tags,
            languages,
            plugins: pluginsData.plugins,
            currentPage: pluginsData.current_page,
            totalPlugins: pluginsData.total_plugins,
            pluginsPerPage: pluginsData.plugins_per_page,
            totalPages: pluginsData.total_pages,
            loadingTags: false,
            loadingLanguages: false,
            loadingPlugins: false
        };
    } catch (error) {
        console.warn('Error in ssrUsePluginsSearch:', error);
        return emptyResponse;
    }
}
